<template>
  <div>
    <h1 class="title is-size-4-mobile">{{ $t("Device management") }}</h1>
    <field-horizontal :label="$t('Add device')">
      <input-field
        v-model="pairingCode"
        :placeholder="$t('Pairing code from device')"
        @focus="pairingCode_onFocus"
        :errors="pairingCode_errorsIfDirty"
      />
      <button
        slot="right"
        class="button is-primary"
        :disabled="!pairingCode_valid"
        @click="addDeviceToProject(pairingCode)"
      >
        {{ $t("Add") }}
      </button>
      <FieldMessage
        slot="message"
        :success="$t(pairingResultSuccessMsg)"
        :failure="$t(pairingResultFailureMsg)"
      />
    </field-horizontal>
    <field-horizontal-table :label="$t('Devices in project')">
      <device-row
        v-for="device in devices"
        :key="device.id"
        :device="device"
        :deviceStatus="devicesStatus[device.id]"
        :failureMsg="deviceChangeNameFailureMsg[device.id]"
        :successMsg="deviceChangeNameSuccessMsg[device.id]"
        @changeName="setDeviceName"
        @delete="openRemoveDeviceConfirmation(device)"
      />
    </field-horizontal-table>
    <modal-confirmation
      :is-open="!!removingDevice"
      :title="$t('Are you sure you want to remove this device?')"
      :confirmText="$t('Remove')"
      @confirm="removeDevice"
      @close="closeRemoveDeviceConfirmation"
    >
      <message v-if="!!removingDeviceError" class="is-danger">{{
        $t(removingDeviceError)
      }}</message>
      {{
        $t(
          "Once removed, device {0} will no longer have granted access to location data!",
          removingDevice.name || removingDevice.id
        )
      }}
    </modal-confirmation>
  </div>
</template>

<script>
import { state, actions } from "@/store"
import { resetTimeout } from "@/utils"
import { validator, size, nonEmpty } from "@/validator"
import {
  InputField,
  FieldHorizontal,
  FieldMessage,
  FieldHorizontalTable
} from "@/components/form"
import DeviceRow from "@/components/settings/device-row"
import { ModalConfirmation } from "@/components/modal"
import message from "@/components/message"

const MSG_TIMEOUT_MS = 3000

export default {
  name: "device-management",
  mixins: [validator],
  components: {
    InputField,
    FieldHorizontal,
    FieldHorizontalTable,
    FieldMessage,
    DeviceRow,
    ModalConfirmation,
    message
  },
  data() {
    return {
      pairingCode: "",
      pairingResultSuccessMsg: undefined,
      pairingResultFailureMsg: undefined,
      deviceChangeNameSuccessMsg: {},
      deviceChangeNameFailureMsg: {},
      removingDevice: "",
      removingDeviceError: undefined
    }
  },
  computed: {
    devices() {
      return state.project.devices
    },
    devicesStatus() {
      return state.project.devicesStatus
    }
  },

  validations: {
    pairingCode: [size(6), nonEmpty]
  },

  methods: {
    addDeviceToProject(code) {
      this.pairingResultFailureMsg = undefined
      this.pairingResultSuccessMsg = undefined
      actions
        .addPairing(code)
        .then(() => {
          this.pairingCode_reset("")
          this.pairingResultSuccessMsg = "Pairing code add succeded"
          resetTimeout(
            "addDeviceToProject",
            () => {
              this.pairingResultSuccessMsg = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          switch (e && e.name) {
            case "pairing_code_exists": {
              this.pairingResultFailureMsg = "duplicit-pairing-code"
              break
            }
            default:
              this.pairingResultFailureMsg = "add-pairing-code-error"
          }
          this.$logger.error(this.pairingResultFailureMsg, e)
        })
    },
    setDeviceName(deviceId, deviceName) {
      this.$set(this.deviceChangeNameFailureMsg, deviceId, undefined)
      this.$set(this.deviceChangeNameSuccessMsg, deviceId, undefined)
      actions
        .setDeviceName(deviceId, deviceName.trim())
        .then(() => {
          this.deviceChangeNameSuccessMsg[deviceId] = "Saved"
          resetTimeout(
            "setDeviceName_" + deviceId,
            () => {
              this.deviceChangeNameSuccessMsg[deviceId] = undefined
            },
            MSG_TIMEOUT_MS
          )
        })
        .catch(e => {
          this.deviceChangeNameFailureMsg[deviceId] = "save-device-name-error"
          this.$logger.error("save-device-name-error", deviceId, e)
        })
    },
    removeDevice() {
      this.removingDeviceError = undefined
      actions
        .removeDeviceFromProject(this.removingDevice.id)
        .then(this.closeRemoveDeviceConfirmation)
        .catch(e => {
          this.removingDeviceError = "remove-device-error"
          this.$logger.error("remove-device-error", this.removingDevice.id, e)
        })
    },
    openRemoveDeviceConfirmation(device) {
      this.removingDevice = device
    },
    closeRemoveDeviceConfirmation() {
      this.removingDevice = ""
    }
  }
}
</script>
